import { Gamepad2, Clock, Calendar } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { Game } from '../../types/api'
import CategoryTag from '../common/CategoryTag'
import GameOptionsDropdown from '../common/GameOptionsDropdown'

interface DevelopmentGameCardProps {
  project: Game
  index: number
  onArchive: (project: Game) => void
  onEdit: (project: Game) => void
  onDuplicate: (project: Game) => Promise<void>
  onTransfer?: (project: Game) => void
  isActionInProgress: boolean
  formatTimeAgo: (dateString: string) => string
}

export const DevelopmentGameCard = ({
  project,
  index,
  onArchive,
  onEdit,
  onDuplicate,
  onTransfer,
  isActionInProgress,
  formatTimeAgo
}: DevelopmentGameCardProps) => {
  const navigate = useNavigate()
  const projectId = project.id
  
  // Get last edit info (for Studio projects) or use fallback
  const lastEdit = project.last_edit
  const displayComment = lastEdit?.comment || "No recent edits"
  const authorName = lastEdit?.author_name || project.owner_display_name || 'Unknown'
  const authorAvatar = lastEdit?.author_avatar || project.owner_avatar

  return (
    <div
      className="group relative bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl p-4 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200 cursor-pointer"
      onClick={() => navigate(`/editor/${projectId}`)}
    >
      <div className="flex items-start gap-4">
        {/* Game Thumbnail */}
        <div className="w-28 h-28 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
          {project.content?.icon_url ? (
            <img 
              src={project.content.icon_url} 
              alt={`${project.name} icon`}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
                const parent = e.currentTarget.parentElement
                if (parent) {
                  parent.innerHTML = '<svg class="w-12 h-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="6" x2="10" y1="12" y2="12"/><line x1="8" x2="8" y1="10" y2="14"/><line x1="15" x2="15.01" y1="13" y2="13"/><line x1="18" x2="18.01" y1="11" y2="11"/><rect width="20" height="16" x="2" y="4" rx="2"/></svg>'
                }
              }}
            />
          ) : (
            <Gamepad2 className="w-12 h-12 text-gray-400" />
          )}
        </div>

        {/* Game Info */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-2 truncate">
            {project.name}
          </h3>
          
          {/* Tags */}
          <div className="flex gap-2 flex-wrap mb-3">
            {project.content?.tags && project.content.tags.length > 0 ? (
              project.content.tags.slice(0, 3).map((tag, tagIndex) => (
                <CategoryTag key={`tag-${tagIndex}`} categoryId={tag} size="sm" showIcon={true} />
              ))
            ) : project.content?.category ? (
              <CategoryTag categoryId={project.content.category} size="sm" showIcon={true} />
            ) : (
              <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                Game
              </span>
            )}
          </div>
          
          {/* Metadata Row */}
          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-2">
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>Edited {formatTimeAgo(project.updated_at)}</span>
            </div>
            {project.created_at && project.updated_at !== project.created_at && (
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>Created {formatTimeAgo(project.created_at)}</span>
              </div>
            )}
          </div>
          
          {/* Last Edit Comment */}
          <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400 italic">
            {/* Author Avatar */}
            {authorAvatar ? (
              <img 
                src={authorAvatar} 
                alt={authorName}
                className="w-5 h-5 rounded-full flex-shrink-0 object-cover"
              />
            ) : (
              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                {authorName?.charAt(0).toUpperCase() || 'U'}
              </div>
            )}
            <span className="line-clamp-1">{displayComment}</span>
          </div>
        </div>

        {/* More Actions */}
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <GameOptionsDropdown
            project={project}
            onArchive={onArchive}
            onDuplicate={onDuplicate}
            onTransfer={onTransfer}
            isActionInProgress={isActionInProgress}
            stopPropagation={true}
            size="sm"
            buttonClassName="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          />
        </div>
      </div>
    </div>
  )
}

